import { useEffect, useSyncExternalStore } from 'react';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';

/**
 * `unknown` until the one read per page load answers; operators reload to follow a flip (SSD-7096), except
 * that a V1 stock write refused with 410 turns it `on` in place (SSD-7433).
 */
export type FulfillmentV2LaunchState = 'unknown' | 'off' | 'on';

let state: FulfillmentV2LaunchState = 'unknown';
let pending: Promise<boolean> | null = null;
const listeners = new Set<() => void>();

const settle = (launched: boolean): boolean => {
  // A 410 already proved the switch on; a read still in flight (or failing) must not turn the tab back to V1.
  if (state === 'on') {
    return true;
  }
  state = launched ? 'on' : 'off';
  listeners.forEach((listener) => listener());
  return launched;
};

/**
 * The Fulfillment V2 launch switch as the Admin dashboard reads it. Anything short of an explicit
 * `launched: true` reads as off, so a failed read or a Facade without the route leaves every screen on
 * V1, exactly as before the switch existed, until a V1 stock write answers 410 (see `markLaunched`).
 */
export const FulfillmentV2Launch = {
  current: (): FulfillmentV2LaunchState => state,

  isLaunched(): Promise<boolean> {
    pending ??= new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT))
      .getLaunchState()
      .then((view) => view?.launched === true)
      .catch(() => false)
      .then(settle);
    return pending;
  },

  /**
   * A V1 write just answered 410, which the server only does once the switch is on (SSD-7433). A tab that
   * read the switch before the flip, or whose read failed, takes that answer as the switch: retired V1
   * screens then hand over to 2.0 instead of offering more V1 writes that can only be refused.
   */
  markLaunched(): void {
    pending = Promise.resolve(true);
    settle(true);
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** Tests only: forget the read so the next one asks again. */
  reset(): void {
    state = 'unknown';
    pending = null;
  },
};

/** The launch state for rendering; asks once per page load and re-renders when the answer lands. */
export const useFulfillmentV2Launch = (): FulfillmentV2LaunchState => {
  const current = useSyncExternalStore(FulfillmentV2Launch.subscribe, FulfillmentV2Launch.current);
  useEffect(() => {
    void FulfillmentV2Launch.isLaunched();
  }, []);
  return current;
};
